/**
 * @fileoverview Движение снежного фронта и накопление снега/льда.
 */

import { FRONT_STAGES } from "../constants/front";
import { W } from "../constants/world";
import { frontStage } from "../state/createGame";
import type { GameHooks, GameState } from "../state/types";
import { clamp, lerp } from "../util/math";
import { addScore } from "./scoring";

/** Покрытие фронтом в точке (0…intensity×dens). */
export function frontCoverage(g: GameState, x: number, y: number): number {
  void y;
  const f = g.front;
  const dx = (x - f.x) / (f.width * 0.5);
  return clamp(1 - Math.abs(dx), 0, 1) * f.intensity * frontStage(g).dens;
}

/** Ведущий край фронта сильнее бьет «свою» сторону поля. */
export function frontEdgeBias(g: GameState, x: number): number {
  const f = g.front;
  if (f.dir > 0) return x < 780 ? 1.35 : x > 980 ? 0.75 : 1;
  return x > 820 ? 1.35 : x < 620 ? 0.75 : 1;
}

/** Синхронизировать зоны ВПП с runtime-состоянием полос. */
export function syncRwyZones(g: GameState): void {
  for (const z of g.zones) {
    if (z.kind === "rwy" && z.rwy !== undefined) {
      const r = g.runways[z.rwy];
      if (!r) continue;
      z.snow = r.snow;
      z.ice = r.ice || 0;
      z.open = !r.closed;
      z.closedReason = r.closed ? "уборка" : "";
    }
  }
}

/** Тик фронта: движение, стадии, накопление снега. */
export function updateFront(
  g: GameState,
  hooks: GameHooks,
  dt: number,
): void {
  const f = g.front;
  const st = frontStage(g);
  const cfg = g.cfg;

  f.stageT += dt;
  f.x += f.dir * f.speed * (0.7 + st.dens * 0.35) * dt;
  f.intensity = lerp(f.intensity, 0.35 + st.dens * 0.4, dt * 0.4);
  g.visTarget = st.vis * (1 + Math.max(0, f.forecastBias));

  if ((f.dir > 0 && f.x > W + f.width) || (f.dir < 0 && f.x < -f.width)) {
    if (cfg.endless) {
      f.wave++;
      f.dir *= -1;
      f.x = f.dir > 0 ? -200 : W + 200;
      f.stage = 0;
      f.stageT = 0;
      f.speed = 38 * cfg.front * (1 + f.wave * 0.08);
      f.stageDur = Math.max(28, 55 / cfg.front - f.wave * 2);
      g.spawnT = Math.max(1.5, g.spawnT);
      hooks.toast(`Волна ${f.wave}: новый снежный фронт надвигается`, "wx");
      g.storyBits.push(`Пережита волна ${f.wave - 1}`);
      addScore(g, hooks, 120 + f.wave * 40, null, `ВОЛНА ${f.wave - 1}`);
    } else {
      f.x = clamp(f.x, -f.width, W + f.width);
    }
  }

  if (f.stageT >= f.stageDur) {
    f.stageT = 0;
    if (f.stage < FRONT_STAGES.length - 1) {
      f.stage++;
      const name = FRONT_STAGES[f.stage]!.name;
      hooks.toast(
        `Стадия фронта: ${name} · идет ${f.dir > 0 ? "с запада на восток" : "с востока на запад"}`,
        "wx",
      );
      if (FRONT_STAGES[f.stage]!.id === "peak") {
        g.bestDecision = g.bestDecision || "Подготовка к пику";
        hooks.toast(
          "Пик метели — держите хотя бы одну полосу рабочей",
          "bad",
        );
      }
    } else if (cfg.endless) {
      f.stage = 0;
    }
  }

  for (const z of g.zones) {
    const cx = z.x + z.w / 2;
    const cy = z.y + z.h / 2;
    const cov = frontCoverage(g, cx, cy);
    const bias = frontEdgeBias(g, cx);
    const rate =
      g.snowRate * (0.35 + cov * 1.4) * (1 + g.snowBoost) * bias;
    if (z.kind === "rwy" && z.rwy !== undefined) {
      const r = g.runways[z.rwy];
      if (!r) continue;
      if (!r.closed) r.snow = Math.min(100, r.snow + rate * dt);
      const iceWas = r.ice || 0;
      if (cov > 0.2) {
        r.ice = Math.min(100, iceWas + st.ice * cov * dt * 8 * bias);
      }
      if (iceWas < 6 && (r.ice || 0) >= 6 && !g._iceHint) {
        g._iceHint = true;
        hooks.toast(
          `${r.name}: гололед на полосе ≥6% — жмите «Реагент» (−12% склада), иначе посадка/взлет рискованнее`,
          "wx",
        );
      }
      if (iceWas < 40 && (r.ice || 0) >= 40 && !g._iceRiskHint) {
        g._iceRiskHint = true;
        hooks.toast(
          `${r.name}: сильный гололед — операции с подтверждением риска. Срочно «Реагент»`,
          "bad",
        );
      }
    } else {
      z.snow = Math.min(100, z.snow + rate * dt * 0.85);
      if (cov > 0.15) {
        z.ice = Math.min(100, z.ice + st.ice * cov * dt * 7 * bias);
      }
    }
  }
  syncRwyZones(g);
}
